import * as React from 'react'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const toggleGroupVariants = cva(
  'inline-flex items-center justify-center overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-gray-200 dark:border-border bg-gray-50 dark:bg-muted rounded-md',
      },
      spacing: {
        default: 'gap-0',
        sm: 'gap-0',
        lg: 'gap-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      spacing: 'default',
    },
  },
)

const toggleGroupItemVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 text-gray-900 dark:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 dark:focus-visible:ring-foreground ring-1 ring-gray-200 dark:ring-border data-[state=on]:ring-gray-800 dark:data-[state=on]:ring-foreground',
  {
    variants: {
      variant: {
        default:
          'bg-transparent aria-pressed:bg-gray-900 dark:aria-pressed:bg-foreground aria-pressed:text-white dark:aria-pressed:text-background bg-muted text-muted-foreground hover:bg-white dark:hover:bg-muted-foreground/10 hover:text-gray-900 dark:hover:text-foreground',
        outline:
          'bg-white dark:bg-transparent hover:bg-gray-100 dark:hover:bg-muted-foreground/10 border-r border-gray-200 dark:border-border last:border-r-0 data-[state=on]:bg-gray-900 dark:data-[state=on]:bg-foreground data-[state=on]:text-white dark:data-[state=on]:text-background hover:data-[state=on]:bg-gray-800 dark:hover:data-[state=on]:bg-foreground/85',
      },
      size: {
        default: 'h-9 px-3',
        sm: 'h-8 px-2.5 text-xs',
        lg: 'h-10 px-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupVariants>
>(({ className, variant, spacing, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(toggleGroupVariants({ variant, spacing }), className)}
    {...props}
  />
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleGroupItemVariants>
>(({ className, variant, size, ...props }, ref) => (
  <ToggleGroupPrimitive.Item
    ref={ref}
    className={cn(toggleGroupItemVariants({ variant, size }), className)}
    {...props}
  />
))

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export { ToggleGroup, ToggleGroupItem }
